import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, Sparkles, ArrowRight } from "lucide-react";
import { TOUR_STEPS } from "./tourSteps";

interface TourCompletionModalProps {
  onComplete: () => void;
  clientDisplayName: string | null;
  matterTitle: string | null;
}

const TourCompletionModal: React.FC<TourCompletionModalProps> = ({
  onComplete,
  clientDisplayName,
  matterTitle,
}) => {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Dark backdrop */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Card */}
      <motion.div
        className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full mx-6 overflow-hidden"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-brand via-brand-accent to-emerald-400" />

        <div className="px-8 pt-8 pb-6 text-center">
          {/* Animated checkmark */}
          <motion.div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-5"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4, type: "spring", stiffness: 200, damping: 12 }}
            >
              <CheckCircle className="h-10 w-10 text-brand" />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              You're ready to go!
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              Your first matter{matterTitle ? (
                <>  -  <span className="font-medium text-slate-700">{matterTitle}</span></>
              ) : null}{" "}
              has been created. Your workspace is set up and ready to go.
            </p>
          </motion.div>

          {/* Quick summary */}
          <motion.div
            className="mt-5 bg-slate-50 rounded-xl px-4 py-3 text-left"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-brand-accent" />
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                What you just set up
              </span>
            </div>
            <div className="space-y-1.5">
              {clientDisplayName && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-accent" />
                  <span className="text-slate-600">
                    Client: <span className="font-medium text-slate-800">{clientDisplayName}</span>
                  </span>
                </div>
              )}
              {matterTitle && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-accent" />
                  <span className="text-slate-600">
                    Matter: <span className="font-medium text-slate-800">{matterTitle}</span>
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-accent" />
                <span className="text-slate-600">Documents organized by client &amp; matter</span>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.button
            onClick={onComplete}
            className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-brand hover:bg-brand-hover transition-colors shadow-lg shadow-emerald-200/50"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
          >
            Got it  -  let's go
            <ArrowRight className="h-4 w-4" />
          </motion.button>

          <motion.p
            className="mt-3 text-xs text-slate-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {TOUR_STEPS.length} of {TOUR_STEPS.length} steps complete
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
};

export default TourCompletionModal;
